import { useEffect } from 'react'
import { Head, usePage } from '@inertiajs/react'
import { Eye } from 'lucide-react'
import ClassicTemplate from '@/components/event-show/ClassicTemplate'
import FundraiserTemplate from '@/components/event-show/FundraiserTemplate'
import GalleryTemplate from '@/components/event-show/GalleryTemplate'
import { resolveBackgroundColor } from '@/lib/eventPalette'
import { formatDate, formatTime } from '@/lib/format'
import { rawPageTitle } from '@/lib/pageTitle'
import { track } from '@/lib/track'
import type { DetailOrganization, Event, SharedProps } from '@/types'

type EventShowProps = {
  event: Event
  organization: DetailOrganization | null
  attendee_count: number
  is_attending: boolean
  is_following: boolean
  is_saved: boolean
  google_maps_api_key: string | null
  brand_free?: boolean
  is_preview?: boolean
  noindex?: boolean
}

export default function EventShow({
  event,
  organization,
  is_following,
  is_saved,
  google_maps_api_key,
  brand_free = false,
  is_preview = false,
  noindex = false,
}: EventShowProps) {
  const { app_url } = usePage<SharedProps>().props
  const ogImage = event.cover_image_url || `${app_url}/meta.png`
  const ogUrl = `${app_url}/events/${event.slug}`
  const eventUrl = typeof window !== 'undefined' ? window.location.href : ''
  const isPreview = is_preview
  const isDraftPreview = event.status === 'draft'
  const backgroundColor = resolveBackgroundColor(event.background_color)
  const isFundraiser = event.page_kind === 'fundraiser'
  const previewMeta = [
    organization?.name,
    event.date ? formatDate(event.date) : null,
    formatTime(event.start_time),
  ].filter(Boolean).join(' · ')
  const eventBlurb = event.description
    ? event.description.slice(0, 160)
    : `${event.title} — a charitable ${isFundraiser ? 'fundraiser' : 'gala event'}${brand_free ? '.' : ' on Gala Season.'}`
  const composedDescription = previewMeta ? `${previewMeta} — ${eventBlurb}` : eventBlurb
  const socialDescription = composedDescription.length > 200
    ? `${composedDescription.slice(0, 197)}...`
    : composedDescription

  useEffect(() => {
    if (isDraftPreview || isPreview) return
    track(event.id, 'page_view')
  }, [event.id, isDraftPreview, isPreview])

  const templateProps = {
    event,
    organization,
    isFollowing: is_following,
    isSaved: is_saved,
    googleMapsApiKey: google_maps_api_key,
    brandFree: brand_free,
    eventUrl,
    isPreview,
  }

  return (
    <>
      <Head title={brand_free ? rawPageTitle(event.title) : event.title}>
        {noindex && <meta head-key="robots" name="robots" content="noindex, nofollow" />}
        <meta head-key="description" name="description" content={socialDescription} />
        <meta head-key="og:title" property="og:title" content={brand_free ? event.title : `${event.title} — Gala Season`} />
        <meta head-key="og:description" property="og:description" content={socialDescription} />
        <meta head-key="og:type" property="og:type" content="article" />
        <meta head-key="og:url" property="og:url" content={ogUrl} />
        <meta head-key="og:image" property="og:image" content={ogImage} />
        <meta head-key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta head-key="twitter:title" name="twitter:title" content={brand_free ? event.title : `${event.title} — Gala Season`} />
        <meta head-key="twitter:description" name="twitter:description" content={socialDescription} />
        <meta head-key="twitter:image" name="twitter:image" content={ogImage} />
      </Head>

      {!brand_free && (isDraftPreview || isPreview) && (
        <div className="bg-primary/10 border-b border-primary/20">
          <div className="mx-auto flex max-w-7xl items-center gap-2 px-4 py-2.5 text-base font-medium text-primary sm:px-6 lg:px-8">
            <Eye className="size-4" />
            {isDraftPreview
              ? 'This event is in draft mode and not visible to the public.'
              : 'Preview mode: public actions are disabled.'}
          </div>
        </div>
      )}

      <div className="min-h-screen" style={{ backgroundColor }}>
        {isFundraiser && event.template_style !== 'gallery' ? (
          <FundraiserTemplate
            event={event}
            organization={organization}
            brandFree={brand_free}
            eventUrl={eventUrl}
            isPreview={isPreview}
          />
        ) : event.template_style === 'gallery' ? (
          <GalleryTemplate {...templateProps} />
        ) : (
          <ClassicTemplate {...templateProps} />
        )}
      </div>
    </>
  )
}
